import { useState } from 'react';
import { ArrowLeft, PartyPopper } from 'lucide-react';
import { GAME_IDS, type GameId } from '@gmp/shared-types/browser';
import { Stage } from '@gmp/shared-ui';
import { navigate } from '../app/router';
import { setPrefs, sound, usePrefs } from '../app/prefs';
import { party } from '../net/client';
import { usePartyState } from '../App';
import { GameCard, GameSections } from './GameCard';
import { useEnabledGames } from '../app/serverConfig';
import { ProfileFields } from './Profile';
import { SiteFooter } from '../app/Footer';

export function CreateParty({ initialGame }: { initialGame?: GameId } = {}) {
  const prefs = usePrefs();
  const { status } = usePartyState();
  const [nickname, setNickname] = useState(prefs.nickname);
  const [avatar, setAvatar] = useState(prefs.avatar);
  const [game, setGame] = useState<GameId>(initialGame ?? 'panic-button');
  const enabled = useEnabledGames();
  const [busy, setBusy] = useState(false);
  // Coming from a game's sheet on the home screen: show only that game, with the full list one tap away.
  const [picking, setPicking] = useState(!initialGame);
  const valid = nickname.trim().length > 0;
  const canCreate = valid && !busy && status === 'online';
  const label = status !== 'online' ? 'Connecting…' : busy ? 'Opening the doors…' : 'Open the party';
  function create() {
    setPrefs({ nickname: nickname.trim(), avatar });
    sound.play('submit');
    setBusy(true);
    party.createRoom(nickname.trim(), avatar, game);
    setTimeout(() => setBusy(false), 4000);
  }
  return (
    <Stage>
      <main className="flex-1 flex flex-col gap-6 safe-pad safe-top safe-bottom py-6 max-w-lg w-full mx-auto">
        <header className="flex items-center gap-3">
          <button className="btn btn-sm btn-secondary" onClick={() => navigate({ name: 'home' })} aria-label="Back"><ArrowLeft size={18} /></button>
          <h1 className="font-display font-black text-3xl">Create a party</h1>
        </header>
        <section className="card p-4"><ProfileFields nickname={nickname} avatar={avatar} onNickname={setNickname} onAvatar={setAvatar} /></section>
        <section className="flex flex-col gap-3" aria-label="Pick a game">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display font-black text-xl">First game</h2>
            {!picking && <button className="btn btn-sm btn-secondary" onClick={() => setPicking(true)} data-testid="create-change-game">Change game mode</button>}
          </div>
          {picking && (
            // A second copy of the bottom button, so nobody has to scroll past the whole list to start.
            <button className="btn text-lg" disabled={!canCreate} onClick={create} data-testid="create-submit-top">
              <PartyPopper /> {label}
            </button>
          )}
          {picking
            ? <GameSections ids={GAME_IDS} enabled={enabled} selected={game} card={(id) => <GameCard key={id} id={id} enabled={enabled.includes(id)} selected={game === id} onClick={() => { setGame(id); sound.play('select'); }} />} />
            : <GameCard id={game} selected enabled={enabled.includes(game)} />}
          <p className="text-mute text-sm">You can switch games and change settings in the lobby.</p>
        </section>
        <button className="btn text-xl min-h-16 sticky bottom-4" disabled={!canCreate} onClick={create} data-testid="create-submit">
          <PartyPopper /> {label}
        </button>
        <SiteFooter className="-mt-2" />
      </main>
    </Stage>
  );
}
